import axios from "axios";
import { Product } from "../../../interfaces/products/product";

const API_URL = `${process.env.REACT_APP_API_URL}/v1/product`;
const AMAZON_API_URL = `${process.env.REACT_APP_API_URL}/v1/amazon`;

const productCache = new Map<string, { expiresAt: number; data: Product[] }>();
const productRequests = new Map<string, Promise<Product[]>>();
const PRODUCT_CACHE_TTL = 5 * 60_000;

export interface ProductFilters {
  category?: string;
  subCategory?: string;
  audience?: "boys" | "girls" | "both";
  diwali?: boolean;
  trending?: boolean;
  featured?: boolean;
  search?: string;
}

export interface AmazonProductInfo {
  images: string[];
  amazonRating: number;
  amazonRatingCount: number;
  brand?: string;
  manufacturer?: string;
  features: string[];
}

const normalize = (value: string) =>
  value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^\p{L}\p{N}\s'-]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();

const getSearchTerms = (query: string) => {
  const stopWords = new Set([
    "a", "an", "the", "for", "and", "or", "with", "to", "of", "in", "on",
    "is", "are", "best", "good", "want", "need", "show", "me", "please",
    "mujhe", "mujhko", "mujh", "ke", "ka", "ki", "k", "hai", "chahiye",
    "wala", "wali", "wale", "jo", "ho", "liye", "liya", "ek", "koi",
  ]);

  return Array.from(
    new Set(
      normalize(query)
        .split(/\s+/)
        .map((term) => term.replace(/^['-]+|['-]+$/g, ""))
        .filter((term) => term.length >= 2 && !stopWords.has(term)),
    ),
  ).slice(0, 12);
};

const getSearchText = (product: Product) => {
  const candidate = product as Product & {
    brand?: string;
    manufacturer?: string;
    tags?: string[];
    keywords?: string[];
  };

  return normalize([
    candidate.title,
    candidate.description,
    candidate.category,
    candidate.subCategory,
    candidate.asin,
    candidate.brand,
    candidate.manufacturer,
    ...(candidate.tags || []),
    ...(candidate.keywords || []),
  ].filter(Boolean).join(" "));
};

export const searchProductsLocally = (products: Product[], query: string): Product[] => {
  const normalizedQuery = normalize(query);

  if (!normalizedQuery) return products;

  const terms = getSearchTerms(normalizedQuery);

  return products
    .map((product) => {
      const text = getSearchText(product);
      const title = normalize(product.title || "");
      const description = normalize(product.description || "");
      const category = normalize(product.category || "");
      const subCategory = normalize(product.subCategory || "");

      let score = 0;

      if (text.includes(normalizedQuery)) score += 100;
      if (title.includes(normalizedQuery)) score += 80;
      if (subCategory.includes(normalizedQuery)) score += 55;
      if (category.includes(normalizedQuery)) score += 45;

      terms.forEach((term) => {
        if (title.includes(term)) score += 25;
        if (subCategory.includes(term)) score += 18;
        if (category.includes(term)) score += 14;
        if (description.includes(term)) score += 9;
        if (text.includes(term)) score += 4;
      });

      const matchedTerms = terms.filter((term) => text.includes(term)).length;
      if (terms.length > 1 && matchedTerms === terms.length) score += 45;
      if (terms.length > 1 && matchedTerms >= Math.ceil(terms.length * 0.6)) score += 15;

      return { product, score, matchedTerms };
    })
    .filter(({ score }) => score > 0)
    .sort(
      (a, b) =>
        b.score - a.score ||
        b.product.priorityScore - a.product.priorityScore ||
        a.product.title.localeCompare(b.product.title),
    )
    .map(({ product }) => product);
};

const getCachedProducts = async (filters: ProductFilters = {}): Promise<Product[]> => {
  const normalizedFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== undefined && value !== ""),
  ) as ProductFilters;

  const cacheKey = JSON.stringify(normalizedFilters);
  const cached = productCache.get(cacheKey);

  if (cached && cached.expiresAt > Date.now()) {
    return cached.data;
  }

  const existingRequest = productRequests.get(cacheKey);
  if (existingRequest) return existingRequest;

  const request = axios
    .get(API_URL, { params: normalizedFilters })
    .then((response) => {
      const data = response.data.data as Product[];
      productCache.set(cacheKey, {
        data,
        expiresAt: Date.now() + PRODUCT_CACHE_TTL,
      });
      return data;
    })
    .finally(() => {
      productRequests.delete(cacheKey);
    });

  productRequests.set(cacheKey, request);
  return request;
};

export const getProducts = async (filters: ProductFilters = {}): Promise<Product[]> => {
  const { search: _search, ...browseFilters } = filters;
  const products = await getCachedProducts(browseFilters);

  if (!_search?.trim()) return products;

  return searchProductsLocally(products, _search);
};

export const getProductBySlug = async (slug: string): Promise<Product> => {
  const response = await axios.get(`${API_URL}/${encodeURIComponent(slug)}`);
  return response.data.data;
};

export const getAmazonProductInfo = async (asin: string): Promise<AmazonProductInfo> => {
  const response = await axios.get(`${AMAZON_API_URL}/public-fetch-amazon`, {
    params: { asin },
  });

  const item = response.data?.ItemsResult?.Items?.[0];
  if (!item) {
    return {
      images: [],
      amazonRating: 0,
      amazonRatingCount: 0,
      features: [],
    };
  }

  const imageSources = [
    item.Images?.Primary?.Large?.URL,
    item.Images?.Primary?.Medium?.URL,
    item.Images?.Primary?.Small?.URL,
    ...(item.Images?.Variants || []).flatMap((variant: any) => [
      variant?.Large?.URL,
      variant?.Medium?.URL,
      variant?.Small?.URL,
    ]),
  ].filter((value): value is string => typeof value === "string" && value.length > 0);

  return {
    images: Array.from(new Set(imageSources)),
    amazonRating: Number(item.CustomerReviews?.StarRating?.Value || 0),
    amazonRatingCount: Number(item.CustomerReviews?.Count || 0),
    brand: item.ItemInfo?.ByLineInfo?.Brand?.DisplayValue,
    manufacturer: item.ItemInfo?.ByLineInfo?.Manufacturer?.DisplayValue,
    features: Array.isArray(item.ItemInfo?.Features?.DisplayValues)
      ? item.ItemInfo.Features.DisplayValues
      : [],
  };
};
